import React, { useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { Button } from "@mui/material";
import { toast } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import { COMPANY_TRANSACTION_EXPORT } from "graphql/companyConsole";
import type { CompanyConnection } from "hooks/useCompanyConnection";
import type { TransactionFilterCriteria } from "components/common/transactions/TransactionsTable";
import { completeReport, downloadReport } from "./reportExport";

interface ExportTransaction {
  id: string;
  accountId?: string;
  cardId?: string;
  type: string;
  status: string;
  amount: number;
  currency: string;
  transactionDate: number;
}

export function TransactionExport({
  companyId,
  filters,
}: {
  companyId: string;
  filters: TransactionFilterCriteria;
}) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    const asOf = Date.now();
    const filter = {
      financialAccountId: filters.accountId,
      cardId: filters.cardId,
      issuer: filters.issuer,
      currency: filters.currency,
      type: filters.type,
      status: filters.status,
      metric: filters.metric,
      search: filters.searchTerm,
      startDate: filters.startDate,
      endDate: Math.min(filters.endDate ?? asOf, asOf),
      minAmount: filters.amountRange?.min,
      maxAmount: filters.amountRange?.max,
    };
    try {
      const csv = await completeReport<ExportTransaction>(
        [
          "company_id",
          "transaction_id",
          "financial_account_id",
          "card_id",
          "type",
          "status",
          "amount_minor",
          "currency",
          "transaction_at",
          "as_of",
        ],
        async (after) =>
          (
            await client.query<{
              companyTransactionExport: CompanyConnection<ExportTransaction>;
            }>({
              query: COMPANY_TRANSACTION_EXPORT,
              variables: { companyId, filter, first: 100, after },
              fetchPolicy: "no-cache",
            })
          ).data.companyTransactionExport,
        (row) => [
          [
            companyId,
            row.id,
            row.accountId ?? "",
            row.cardId ?? "",
            row.type,
            row.status,
            row.amount,
            row.currency,
            new Date(row.transactionDate).toISOString(),
            new Date(asOf).toISOString(),
          ],
        ],
        () => !mounted.current,
      );
      downloadReport(csv, `transactions-${companyId}.csv`);
    } catch {
      if (mounted.current)
        toast.error(t("The transaction export could not be completed"));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  return (
    <Button
      variant="default-outlined"
      disabled={busy}
      onClick={() => void run()}
    >
      {t(busy ? "Exporting transactions" : "Export transactions")}
    </Button>
  );
}
